<template>
  <AppCard as="article" class="card">
    <!-- The whole card is the way into its amendment (MOL-42, В-3), and the removal lives in its sheet
         (MOL-189, Ф-34). No `aria-label`: it would replace the name whole, and the rate, the comparison
         and the note would go silent (review С-3). The verb and the day are said first, unseen. Open
         without a connection too (С-19): the sheet says what waits for it. -->
    <button class="body" type="button" @click="$emit('edit', exchange)">
      <span class="verb">{{ t('exchange.edit_on', { date: dayOf(exchange) }) }}</span>
      <OperationCardHead
        :day="headOf(exchange)"
        :amended="
          exchange.amendedAt ? t('exchange.amended', { date: shortDay(exchange.amendedAt) }) : null
        "
      />
      <span class="sums">
        <span class="side">
          <SectionCaption as="span" inset>{{ t('exchange.card_given') }}</SectionCaption>
          <span class="amount">{{ moneyOf(exchange.given) }}</span>
          <span v-if="givenName" class="account">
            {{ t('accounts.card_from', { name: givenName }) }}
          </span>
        </span>
        <span class="arrow" aria-hidden="true"><IconArrow /></span>
        <span class="side received">
          <SectionCaption as="span" inset>{{ t('exchange.card_received') }}</SectionCaption>
          <span class="amount">{{ moneyOf(exchange.received) }}</span>
          <span v-if="receivedName" class="account">
            {{ t('accounts.card_to', { name: receivedName }) }}
          </span>
        </span>
      </span>
      <span class="plate">
        <span v-if="exchange.rate" class="line">
          <span class="label">{{ t('exchange.card_rate') }}</span>
          <span class="value own">{{ rateOf(exchange.rate) }}</span>
        </span>
        <!-- The market first — what one could have got at a counter that day — and the central
             bank under it, quieter: nobody changes at its rate (MOL-137, Р-5). Against the market
             the difference is «хуже / лучше» and takes its tone (Ф-30); against the bank it is a
             difference and nothing more, no tone (Д-5). -->
        <template v-if="market">
          <template v-for="line in marketLines" :key="line.label">
            <span class="line">
              <span class="label">{{ line.label }}</span>
              <span class="value">{{ line.rate }}</span>
            </span>
            <span class="difference" :class="line.tone">{{ line.difference }}</span>
          </template>
          <span v-if="market.pending" class="missing">{{ market.pending }}</span>
        </template>
        <span :class="{ quiet: market }" class="official">
          <template v-if="official">
            <span class="line">
              <span class="label">{{ official.label }}</span>
              <span class="value">{{ official.rate }}</span>
            </span>
            <span class="difference">{{ official.difference }}</span>
          </template>
          <!-- Under a market comparison «nothing to compare with» would be untrue (review). -->
          <span v-else class="missing">{{
            market && !exchange.officialDoubtful
              ? t('exchange.card_no_official_short', { bank: bankOf(exchange) })
              : noOfficialOf(exchange)
          }}</span>
        </span>
      </span>
      <span v-if="exchange.note" class="note">
        <IconNote class="note-icon" aria-hidden="true" />
        <span class="note-text">{{ exchange.note }}</span>
      </span>
    </button>
  </AppCard>
</template>

<script lang="ts">
import { computed, defineComponent } from 'vue'
import type { PropType } from 'vue'
import { useI18n } from 'vue-i18n'
import IconArrow from '~icons/mdi/arrow-right'
import IconNote from '~icons/mdi/note-text-outline'
import type { ExchangeView } from '@molvia/model'
import AppCard from '@/components/AppCard.vue'
import OperationCardHead from '@/components/OperationCardHead.vue'
import SectionCaption from '@/components/SectionCaption.vue'
import { useExchangeWords } from '@/composables/useExchangeWords'
import { dayWords, purchaseDay } from '@/days'
import { useAccountsStore } from '@/stores/accounts'

/**
 * One exchange (MOL-81, handoff 02): «when» on top, «how much» — what was given and what came —
 * large in the middle, «at what rate» on a plate below, the note last. It said all of it before in
 * five lines of small text; the parts now each have a place, and nothing wraps inside a figure.
 */
export default defineComponent({
  name: 'ExchangeCard',
  components: { AppCard, IconArrow, IconNote, OperationCardHead, SectionCaption },
  props: {
    exchange: { type: Object as PropType<ExchangeView>, required: true },
    /** The phone's today, held by the screen and asked again when it comes back (adversarial Н). */
    today: { type: String, required: true },
  },
  emits: {
    edit: (exchange: ExchangeView) => typeof exchange === 'object',
  },
  setup(props) {
    const { t, locale } = useI18n()
    const words = useExchangeWords()
    const accounts = useAccountsStore()
    // Where the money lay, by the name the account has now (MOL-81 Р-6, MOL-123).
    const nameOf = (id: string | null) =>
      id ? (accounts.accounts.find((account) => account.id === id)?.name ?? null) : null
    const market = computed(() => words.marketOf(props.exchange))
    return {
      t,
      ...words,
      givenName: computed(() => nameOf(props.exchange.givenAccountId)),
      receivedName: computed(() => nameOf(props.exchange.receivedAccountId)),
      official: computed(() => words.officialOf(props.exchange)),
      market,
      marketLines: computed(() =>
        market.value ? [market.value.best, ...(market.value.own ? [market.value.own] : [])] : [],
      ),
      shortDay: (when: Date) => purchaseDay(when, locale.value, props.today),
      // «Сегодня», «Вчера» by the phone's today (MOL-121, В-1); the button and the rate say the date.
      headOf: (exchange: ExchangeView) => dayWords(exchange.exchangedOn, locale.value, props.today),
    }
  },
})
</script>

<style scoped lang="scss">
.card {
  padding: 0;
  container-type: inline-size;
}

.body {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  width: 100%;
  padding: var(--space-4);
  border: 0;
  border-radius: var(--radius-lg);
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: var(--surface-2);
  }

  &:focus-visible {
    @include focus-ring(-2px);
  }
}

.verb {
  @include visually-hidden;
}

.sums {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  gap: var(--space-2);
  align-items: start;
}

.side {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.received {
  align-items: flex-end;
  text-align: right;
}

// Two amounts and the arrow need some 290 px; narrower — a 320 px phone — the amounts no longer fit
// side by side and ran over the arrow, since a figure never wraps (review Т-2). Then they stand one
// under the other, both from the left, and the arrow, which only says «into», goes.
@container (max-width: 20rem) {
  .sums {
    grid-template-columns: minmax(0, 1fr);
  }

  .arrow {
    display: none;
  }

  .received {
    align-items: flex-start;
    text-align: left;
  }
}

.account {
  color: var(--text-muted);
  font-size: var(--text-footnote);
  overflow-wrap: anywhere;
}

.amount {
  @include display-type;

  font-size: var(--text-card-figure);
  font-variant-numeric: tabular-nums;
  line-height: var(--leading-snug);
}

.arrow {
  display: grid;
  place-items: center;
  width: calc(var(--space-6) + var(--space-1));
  height: calc(var(--space-6) + var(--space-1));
  margin-top: var(--space-4);
  border-radius: 50%;

  // Only says «into»: terracotta is «press here» (Ф-4).
  background: var(--surface-2);
  color: var(--text-muted);

  svg {
    @include icon;

    font-size: var(--icon-sm);
  }
}

.plate {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius);
  background: var(--surface-2);
  font-size: var(--text-footnote);
}

/* The figure goes under its words only where the two cannot stand side by side even with the words
   broken (MOL-321): «4,80 ֏/₽» stood 5 px past a phone of 320 at 200 %. */
.line {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  column-gap: var(--space-3);

  > :first-child {
    flex: 1 1 min-content;
  }
}

.label,
.missing {
  color: var(--text-muted);
}

.value {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.own {
  font-weight: var(--weight-bold);
}

// The official comparison under the market's: the same lines, a step quieter.
.official {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.quiet {
  padding-top: var(--space-2);
  border-top: var(--hairline) solid var(--border);
  color: var(--text-muted);

  .difference {
    font-weight: var(--weight-regular);
  }
}

// Against the market a minus is worse and a plus better, said in words beside the colour (Ф-30,
// В-11). Against the central bank it stays neutral: nobody changes at its rate, a good exchanger
// beats it, and the difference says nothing about why (MOL-40, В-3).
.difference {
  font-weight: var(--weight-medium);

  &.bad {
    color: var(--bad-ink);
  }

  &.good {
    color: var(--good-ink);
  }
}

.note {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  color: var(--text-muted);
  font-size: var(--text-footnote);
}

.note-icon {
  @include icon;

  font-size: var(--icon-sm);
}

.note-text {
  min-width: 0;
  overflow-wrap: anywhere;
}
</style>
